import {
  ArrowTrendingUpIcon,
  BellAlertIcon,
  BookOpenIcon,
  CalendarIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  IdentificationIcon,
  MegaphoneIcon,
  NewspaperIcon,
  PencilSquareIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

import { ClientOnly } from '@/components/ClientOnly';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Logo } from '@/components/Logo';

import UserDropdown from '@/components/Nav/UserDropdown';
import useMe from './utils/useMe';
import { usePageContext } from './utils/usePageContext';
import { useRasContext } from './utils/useRasContext';

import { MobileTabBar } from '@/components/Nav/MobileTabBar';
import { CollapsibleNavGroup, NavGroupLabel } from '@/components/Nav/NavGroup';
import NavLink from '@/components/Nav/NavLink';
import type { Link } from '@/components/Nav/NavLink';
import { RotatingPromo } from '@/components/Nav/RotatingPromo';
import { request } from '@/renderer/utils/api';
import { isStaffRole } from '@/renderer/utils/isStaffRole';

export default Nav;

// Bounded server counts that the sidebar unread badges seed/reconcile from, so the
// active (in-app) sidebar number equals the inactive (push) badge — both read the
// SAME server definition (server/src/shared/unreadBadge.js). Called only client-side
// from WebSocketUnreadCounter's mount/reconnect/foreground reconcile, never per
// release, so they add no release-time fan-out (#1801). Best-effort: fall back to 0.
async function fetchReleaseUnreadCount(): Promise<number> {
  const res = await request<{ count: number }>('/api/v1/messageReceipt/unread-release-count');
  return Number(res?.count) || 0;
}

async function fetchDmUnreadCount(): Promise<number> {
  const res = await request<{ count: number }>('/api/v1/messageReceipt/unread-dm-count');
  return Number(res?.count) || 0;
}

// Truly standalone pages that carry their own marketing header/footer and never
// show the app sidebar for *anyone* (signed-in or not): the homepage, the auth
// screens, and a few dedicated marketing funnels.
//
// Do NOT add "in-app" content landings here. HIDE_ON removes the sidebar for
// signed-in users too, which strands them with no navigation (the /app
// regression, issue #1405). Those routes belong in MARKETING_LANDING_ROUTES.
const HIDE_ON = [
  '/',
  '/signin',
  '/register',
  '/forgot-password',
  '/links',
  '/mailing-list',
  '/who-is-ras-for',
  // Standalone release-warning unsubscribe confirmation reached from an email
  // link (#1754); the visitor is signed-out and the page carries its own chrome.
  '/unsubscribe',
  // Unlisted CFB Results promo archive (#1495): a self-contained microsite that
  // carries its own full-page chrome (it renders inside a full-viewport iframe),
  // so it must not have the app sidebar layered over it. Not an in-app landing —
  // it is standalone like the homepage. Guarded only by being unlinked, like /vsin.
  '/cfb-results',
];

// Public, shareable "in-app" content landings. Convention (header-vs-sidebar-by-
// auth): signed-out visitors get the marketing header — rendered by the page via
// <MarketingHeaderSlot /> — while signed-in users get the app sidebar below so
// they can navigate the app. Add a route here AND drop <MarketingHeaderSlot /> at
// the top of its +Page.tsx; never add it to HIDE_ON.
const MARKETING_LANDING_ROUTES = ['/order', '/app'];

// ── Grouped nav config ───────────────────────────────────────────────────────
// The sidebar is grouped (Plays / Account / Walls' Blog / Guides / Staff) for
// scannability. Every href and role-gate below is unchanged from the old flat
// list — this is a structural reorg only. `roleRequired` links are filtered per
// group in the render below.

// 1. Plays — the primary, full-weight group at the top.
const PLAYS_LINKS: Link[] = [
  {
    count: {
      dataPath: '/release-center',
      dataName: 'messageReceipt/created',
      getUnreadCount: fetchReleaseUnreadCount,
    },
    href: '/release-center',
    icon: MegaphoneIcon,
    text: 'Release Center',
  },
  // One "Results" entry: signed-in members go to their own results (/results, which
  // defaults to My with an All toggle); signed-out visitors go to the public archive
  // (/archive). Two links, only one shown per auth state (#2004).
  {
    href: '/results',
    startsWithHref: '/results',
    icon: ArrowTrendingUpIcon,
    text: 'Results',
    badge: 'pending',
    roleRequired: true,
  },
  {
    href: '/archive',
    startsWithHref: '/archive',
    icon: ChartBarSquareIcon,
    text: 'Results',
    hideWhenSignedIn: true,
  },
  {
    href: '/release-schedule',
    icon: CalendarIcon,
    text: 'Release Schedule',
  },
];

// 2. Account — Order + Support Chat. The "Active services" summary is folded in
// under this group in the render (no longer a floating standalone block).
const ACCOUNT_LINKS: Link[] = [
  {
    href: '/order',
    startsWithHref: '/order',
    icon: ShoppingBagIcon,
    text: 'Order',
  },
  {
    count: {
      dataPath: '/support-chat',
      dataName: 'directMessageReceipt/created',
      dataCb: (_event, json, setCount, map, me) => {
        if (me?.role === 'admin' || me?.role === 'staff') {
          // I'm an admin receiving a DM notification
          if (
            (json.data.user?.role === 'admin' || json.data.user?.role === 'staff') &&
            json.data.receiverUserId
          ) {
            // When an admin messages a user, we clear all the notifications for this chat for everyone

            const path = `/support-chat/${json.data.receiverUserId}`;
            const pathCount = map.current.get(path);

            if (pathCount) {
              setCount((count) => Math.max(count - pathCount, 0));
              map.current.delete(path);
            }
          } else {
            // When a regular user messages admins, we increment the count if we aren't on that page already
            const path = `/support-chat/${json.data.userId}`;
            const pathCount = map.current.get(path);

            // 1647 is Cleatus
            if (!pathCount && location.pathname !== path && path !== '/support-chat/1647') {
              map.current.set(path, 1);
              setCount((count) => count + 1);
            }
          }
        } else {
          // I'm a regular user receiving a DM
          const path = '/support-chat';
          const pathCount = map.current.get(path);

          if (json.data.userId === me?.id) {
            // If I got it from myself, set unread to 0
            map.current.delete(path);
            setCount(0);
          } else {
            // Otherwise
            map.current.set(path, (pathCount || 0) + 1);
            setCount((count) => count + 1);
          }
        }
      },
      dataNavCb(path, setCount, map) {
        const pathCount = map.current.get(path);

        if (pathCount) {
          setCount((count) => Math.max(count - pathCount, 0));
          map.current.delete(path);
        }
      },
      // Seed/reconcile the member support-chat badge from the same server unread-DM
      // count the push badge uses (unreadDmBadgeCount). Ignored for admin/staff,
      // whose sidebar counts distinct unread threads via dataCb above, not this
      // raw inbound-DM total (WebSocketUnreadCounter gates seeding on role).
      getUnreadCount: fetchDmUnreadCount,
    },
    href: '/support-chat',
    startsWithHref: '/support-chat',
    icon: ChatBubbleLeftRightIcon,
    text: 'Support Chat',
  } as Link<API.DirectMessageReceipt>,
];

// 3. Walls' Blog — standalone content link (NOT a guide), rendered full-weight
// on its own after Account.
const WALLS_LINK: Link = {
  href: '/walls',
  startsWithHref: '/walls',
  icon: NewspaperIcon,
  text: "Walls' Blog",
};

// 4. Guides — collapsed-by-default reference group.
const GUIDES_LINKS: Link[] = [
  {
    href: '/welcome-guide',
    icon: BookOpenIcon,
    text: 'Welcome Guide',
  },
  {
    href: '/alerts-guide',
    icon: BellAlertIcon,
    text: 'Alert Guide',
  },
  {
    href: '/about',
    icon: IdentificationIcon,
    text: 'About RAS',
  },
];

type TempLink = {
  href: string;
  text: string;
};

const TEMPLINKS: TempLink[] = import.meta.env.DEV
  ? [
      {
        href: '/signin',
        text: 'Sign in',
      },
      {
        href: '/register',
        text: 'Sign Up',
      },
      {
        href: '/forgot-password',
        text: 'Forgot Password',
      },
      {
        href: '/404',
        text: '404',
      },
      {
        href: '/',
        text: 'Index',
      },
    ]
  : [];

function Nav({ withoutUser = false }) {
  const { urlParsed, urlPathname } = usePageContext();

  const rasContext = useRasContext();
  // biome-ignore lint/correctness/useHookAtTopLevel: This is used in the <Suspense> of Nav, so it never changes
  const me = withoutUser ? null : useMe();
  const user = me || rasContext.data?.user;

  // Drop role-gated links (e.g. My Results) for signed-out visitors, then render
  // through NavLink. Applied per group below.
  const renderLinks = (links: Link[]) =>
    links
      .filter(
        (link) => !(link.roleRequired && !user?.role) && !(link.hideWhenSignedIn && user?.role),
      )
      .map((link) => <NavLink key={link.href + link.text} link={link} urlParsed={urlParsed} />);

  const showStaff = me?.role === 'author' || me?.role === 'admin' || me?.role === 'staff';

  if (HIDE_ON.includes(urlPathname)) {
    return null;
  }

  // Marketing landing routes show the marketing header (not the sidebar) to
  // signed-out visitors, but signed-in users keep the app sidebar so they can
  // navigate out. Match the route exactly or as a path prefix (e.g. /order/123)
  // without over-matching siblings like /apply.
  const isMarketingLanding = MARKETING_LANDING_ROUTES.some(
    (route) => urlPathname === route || urlPathname.startsWith(`${route}/`),
  );

  if (isMarketingLanding && !user) {
    return null;
  }

  return (
    <>
      {/* Desktop-only sidebar. Below `md` it is `display: none` and the bottom
          tab bar (and its More sheet) is the only primary navigation on phones,
          web and native alike (#2142); there is no phone drawer any more. It
          stays MOUNTED on phones on purpose: its unread counters
          (WebSocketUnreadCounter) and the pending badge are the single writers
          the tab bar badges read from, so hiding it with CSS (not unmounting
          it) keeps one count per source. Safe-area padding lives on the
          <aside> itself so the dark bg fills the notch/home-indicator bands on
          a native iPad (issue #1453); insets are 0 elsewhere. */}
      <aside
        id="ras-sidebar"
        aria-label="Sidebar"
        className="peer/nav fixed top-0 left-0 z-40 hidden h-[100dvh] w-72 flex-none flex-col bg-zinc-900 pt-[var(--safe-top)] pb-[var(--safe-bottom)] text-sm text-white select-none md:flex dark:border-r dark:border-white/10 dark:bg-zinc-950"
      >
        <article className="flex grow flex-col space-y-5 overflow-y-auto overscroll-contain p-4 px-6">
          <div className="flex items-center justify-between">
            <Logo className="size-10 select-none" />
            <a href="/settings" aria-label="Account settings">
              <Cog6ToothIcon
                height={24}
                className="size-6 shrink-0 text-zinc-400 hover:text-white active:text-white"
                aria-hidden="true"
              />
            </a>
          </div>
          <ErrorBoundary>
            <ClientOnly component={() => import('@/components/Nav/AppPromoCard')} />
          </ErrorBoundary>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col space-y-6">
              {/* 1. Plays */}
              <li>
                <NavGroupLabel>Plays</NavGroupLabel>
                <ul role="list" className="-mx-2 mt-1 space-y-1">
                  {renderLinks(PLAYS_LINKS)}
                </ul>
              </li>

              {/* 1b. Staff — role-gated cluster (Editor + Admin), kept directly
                  under Plays so admins reach it without scrolling (#1405 lineage). */}
              {showStaff && (
                <li>
                  <NavGroupLabel>Staff</NavGroupLabel>
                  <ul role="list" className="-mx-2 mt-1 space-y-1">
                    {/* Admin entry — only visible to admin/staff */}
                    {(me?.role === 'admin' || me?.role === 'staff') && (
                      <li>
                        <a
                          href="/admin"
                          data-active={urlParsed.pathnameOriginal.startsWith('/admin')}
                          className="flex w-full items-center gap-x-3 rounded px-3 py-2 leading-6 font-semibold text-amber-300 transition-[background-color,color] duration-150 hover:bg-amber-400/15 hover:text-amber-200 active:bg-amber-400/20 data-[active=true]:bg-amber-400/20 data-[active=true]:text-amber-200 data-[active=true]:ring-1 data-[active=true]:ring-amber-400/30 data-[active=true]:ring-inset"
                        >
                          <WrenchScrewdriverIcon className="size-6 shrink-0" aria-hidden="true" />
                          <span className="flex-1 whitespace-nowrap">Admin</span>
                        </a>
                      </li>
                    )}

                    {/* Editor entry — Walls' blog CMS, only for authors/admins */}
                    {(me?.role === 'author' || me?.role === 'admin') && (
                      <li>
                        <a
                          href="/editor"
                          data-active={urlParsed.pathnameOriginal.startsWith('/editor')}
                          className="flex w-full items-center gap-x-3 rounded px-3 py-2 leading-6 font-semibold text-emerald-300 transition-[background-color,color] duration-150 hover:bg-emerald-400/15 hover:text-emerald-200 active:bg-emerald-400/20 data-[active=true]:bg-emerald-400/20 data-[active=true]:text-emerald-200 data-[active=true]:ring-1 data-[active=true]:ring-emerald-400/30 data-[active=true]:ring-inset"
                        >
                          <PencilSquareIcon className="size-6 shrink-0" aria-hidden="true" />
                          <span className="flex-1 whitespace-nowrap">Editor</span>
                        </a>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {/* 2. Account — links + folded-in Active services summary */}
              <li>
                <NavGroupLabel>Account</NavGroupLabel>
                <ul role="list" className="-mx-2 mt-1 space-y-1">
                  {/* The Order link is shown on iOS too now: /order renders the
                      In-App Purchase surface there (#2100), not Stripe checkout. */}
                  {renderLinks(ACCOUNT_LINKS)}
                </ul>
                <ErrorBoundary>
                  <ClientOnly component={() => import('@/components/Nav/ActiveServices')} />
                </ErrorBoundary>
              </li>

              {/* 3. Walls' Blog — standalone content link (not a guide) */}
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  <NavLink link={WALLS_LINK} urlParsed={urlParsed} />
                </ul>
              </li>

              {/* 4. Guides — collapsed by default */}
              <li>
                <CollapsibleNavGroup
                  label="Guides"
                  links={GUIDES_LINKS}
                  urlParsed={urlParsed}
                  storageKey="ras-nav-guides"
                />
              </li>

              {/* Temporary Links (dev only) */}
              {TEMPLINKS.length > 0 && (
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {TEMPLINKS.map((link) => {
                      return (
                        <li key={link.href + link.text}>
                          <a
                            href={link.href}
                            data-active={urlParsed.pathnameOriginal === link.href}
                            className="flex w-full items-center rounded-sm p-2 text-zinc-400 transition-[width,color,background-color] duration-500 ease-out hover:bg-white/5 hover:text-white hover:duration-75 active:bg-white/5 active:text-white data-[active=true]:bg-white/10 data-[active=true]:text-white"
                          >
                            <span className="ml-3 flex-1 whitespace-nowrap">{link.text}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              )}
            </ul>
          </nav>
        </article>
        <article className="mt-auto flex grow-0 flex-col space-y-1 p-2">
          {/* Gated off inside the iOS app by RotatingPromo itself. */}
          <RotatingPromo />
          <hr className="h-px border-0 bg-white/10" />
          <div className="flex grow justify-between gap-x-1.5">
            <ErrorBoundary>
              <ClientOnly
                component={() => import('@/components/Nav/UserDropdown')}
                key="client-user"
              >
                <UserDropdown withoutUser={true} user={user} key="server-user" />
              </ClientOnly>
              <ClientOnly component={() => import('@/components/UI/DesktopNotifications')} />
            </ErrorBoundary>
          </div>
        </article>
      </aside>

      {/* Mobile primary navigation. Rendered HERE, below both of the gates above
          (HIDE_ON and the marketing-landing check), so the tab bar appears on
          exactly the pages the sidebar appears on and vanishes on exactly the
          pages it vanishes on: the one rule that keeps a signed-in user from
          ending up on a guarded route with no navigation at all (#1405). It is
          `md:hidden` and the sidebar is `hidden md:flex`, so at every width
          exactly one of the two is the page's navigation. */}
      <MobileTabBar
        urlPathname={urlPathname}
        signedIn={!!user?.role}
        // Same role check, from the same source (`me`), as the sidebar's Admin
        // link above, so the two never disagree while the session loads.
        isStaff={isStaffRole(me?.role)}
      />
    </>
  );
}
